import * as stylex from '@stylexjs/stylex';
import { LABEL_BASELINE_SHIFT } from './ProgressCircle.constants';
import { progressCircleStyles as styles } from './ProgressCircle.styles';
import { labelFontSize, roundLabel } from './ProgressCircle.utilities';
import type { ProgressCircleProps } from './ProgressCircle.types';

/**
 * A circular (ring) progress indicator with a centered percentage label. `showFraction` and `rounding` format only the
 * label; the ring and accessible value always reflect the unrounded `value`.
 */
const ProgressCircle = ({
  value,
  showFraction,
  rounding,
  size = 'md',
  xstyle,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: ProgressCircleProps) => {
  if (!Number.isFinite(value)) return null;
  const percentage = Math.min(100, Math.max(0, value));
  const labelValue = roundLabel(percentage, { showFraction, rounding });
  const label = String(labelValue);

  return (
    <span
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percentage}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      {...stylex.props(styles.root, styles[size], xstyle)}>
      <svg viewBox="0 0 100 100" {...stylex.props(styles.circle)} aria-hidden="true">
        <circle cx="50" cy="50" r="46" fill="none" strokeWidth="8" {...stylex.props(styles.trail)} />
        <circle
          cx="50"
          cy="50"
          r="46"
          fill="none"
          strokeWidth="8"
          pathLength="100"
          strokeDasharray="100"
          transform="rotate(-90 50 50)"
          {...stylex.props(styles.progress, styles.progressValue(percentage))}
        />
        <text
          x="50"
          y="50"
          dy={LABEL_BASELINE_SHIFT}
          {...stylex.props(
            styles.label,
            styles.labelSize(labelFontSize(label, { isFraction: !Number.isInteger(labelValue) })),
          )}>
          {label}
        </text>
      </svg>
    </span>
  );
};

export default ProgressCircle;
